import { c as _c } from "react/compiler-runtime";
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Badge } from '@sola/ark-core/atoms/badge/badge.tsx';
import { ButtonStyle } from '@sola/ark-core/atoms/button-style/button-style.tsx';
import { Link } from '@sola/ark-core/atoms/link/link.tsx';
import { Alert } from '@sola/ark-core/molecules/alert/alert.tsx';
import { Dialog, DialogActionPrimary, DialogActions, DialogClose, DialogContent, DialogTrigger } from '@sola/ark-core/molecules/dialog/dialog.tsx';
import { ProgressBar } from '@sola/ark-core/molecules/progress-bar/progress-bar.tsx';
import { TooltipInfo } from '@sola/ark-core/molecules/tooltip-info/tooltip-info.tsx';
import { ArkIcon } from '@sola/ark-icons/lib/ark-icon.tsx';
import type { GiftOption } from '../../types/gifts.ts';
interface GiftActionButtonProps {
  buttonText?: string;
  onClick?: () => void;
}
const GiftActionButton: React.FC<GiftActionButtonProps> = t0 => {
  const $ = _c(3);
  const {
    buttonText,
    onClick
  } = t0;
  if (!buttonText) {
    return null;
  }
  let t1;
  if ($[0] !== buttonText || $[1] !== onClick) {
    t1 = <ButtonStyle variant="secondary" className="align-start" textwrap={true}><button type="button" onClick={onClick}>{buttonText}</button></ButtonStyle>;
    $[0] = buttonText;
    $[1] = onClick;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  return t1;
};
const GiftRemoveDialog: React.FC<{
  openTrigger: React.ReactElement;
  onRemove?: () => Promise<void>;
}> = ({
  openTrigger,
  onRemove
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSubmitError, setShowSubmitError] = useState(false);
  const {
    t
  } = useTranslation('my');
  const handleRemove = async () => {
    if (isSubmitting) {
      return;
    }
    setShowSubmitError(false);
    try {
      setIsSubmitting(true);
      await onRemove?.();
      setIsOpen(false);
    } catch {
      // Do not close the dialog, display the error to the user
      setShowSubmitError(true);
    } finally {
      setIsSubmitting(false);
    }
  };
  return <Dialog open={isOpen} onOpenChange={(nextOpen: boolean) => {
    setIsOpen(nextOpen);
    setShowSubmitError(false);
  }}>
      <DialogTrigger>{openTrigger}</DialogTrigger>
      <DialogContent purpose="negative" icon="info-circle" title={t('my:giftCommitmentCard.removeGiftDialog.title')} supportingText={t('my:giftCommitmentCard.removeGiftDialog.supportingText')} size="sm">
        {showSubmitError && <Alert className="mb-comfy-lg" variant="negative" title={t('my:giftCommitmentCard.removeGiftDialog.error.title')} message={t('my:giftCommitmentCard.removeGiftDialog.error.message')} action={<Link href={t('my:ctas.contactSupport.url')}>
                {t('my:ctas.contactSupport.text')}
              </Link>} />}
        <DialogActions>
          <ButtonStyle className="w-full md:w-fit" size="md" purpose="neutral" variant="secondary" disabled={isSubmitting}>
            <DialogClose>
              {t('my:giftCommitmentCard.removeGiftDialog.cancel')}
            </DialogClose>
          </ButtonStyle>
          <DialogActionPrimary purpose="negative" onClick={handleRemove} isBusy={isSubmitting} disabled={isSubmitting}>
            {t('my:giftCommitmentCard.removeGiftDialog.remove')}
          </DialogActionPrimary>
        </DialogActions>
      </DialogContent>
    </Dialog>;
};
export const GivingCardAction: React.FC<{
  giftOption: GiftOption;
  childName?: string;
  onGiftOptionsOpen?: () => void;
  onGiftRemove?: (cartItemId: string) => Promise<void>;
}> = t0 => {
  const $ = _c(37);
  const {
    giftOption,
    childName,
    onGiftOptionsOpen,
    onGiftRemove
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = ["my", "core"];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    t
  } = useTranslation(t1);
  const {
    tooltip,
    progressLabel,
    buttonAction,
    cartItem,
    badge
  } = giftOption;
  const hasCartItem = Boolean(cartItem);
  const showStandaloneButton = Boolean(buttonAction) && !progressLabel && !hasCartItem && !tooltip;
  const showTooltipAction = tooltip && !hasCartItem && !progressLabel;
  let t2;
  if ($[1] !== childName || $[2] !== progressLabel?.given || $[3] !== progressLabel?.max || $[4] !== t || $[5] !== tooltip) {
    t2 = tooltip ? t(`my:giftCommitmentCard.tooltipMessages.${tooltip.key}`, {
      childName,
      currentGifts: progressLabel?.given,
      count: tooltip.count ?? progressLabel?.max
    }) : undefined;
    $[1] = childName;
    $[2] = progressLabel?.given;
    $[3] = progressLabel?.max;
    $[4] = t;
    $[5] = tooltip;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  const tooltipMessage = t2;
  let t3;
  if ($[7] !== buttonAction || $[8] !== t) {
    t3 = buttonAction ? t(`my:giftCommitmentCard.buttonText.${buttonAction}`) : undefined;
    $[7] = buttonAction;
    $[8] = t;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  const buttonText = t3;
  let t4;
  if ($[10] !== buttonText || $[11] !== onGiftOptionsOpen) {
    t4 = {
      buttonText,
      onClick: onGiftOptionsOpen
    };
    $[10] = buttonText;
    $[11] = onGiftOptionsOpen;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  const actionButtonProps = t4;
  let t5;
  if ($[13] !== actionButtonProps || $[14] !== progressLabel || $[15] !== t || $[16] !== tooltipMessage) {
    t5 = progressLabel && <div className="flex flex-col gap-comfy-sm" style={{
      minWidth: 187
    }}><ProgressBar label={t(progressLabel.labelKey, {
        currentGifts: progressLabel.given,
        count: progressLabel.max,
        ordinal: t("core:numbers.suffix", {
          count: progressLabel.given,
          ordinal: true
        })
      })} value={progressLabel.given} minValue={0} maxValue={progressLabel.max} tooltipMessage={tooltipMessage} showValueLabel={false} /><GiftActionButton {...actionButtonProps} /></div>;
    $[13] = actionButtonProps;
    $[14] = progressLabel;
    $[15] = t;
    $[16] = tooltipMessage;
    $[17] = t5;
  } else {
    t5 = $[17];
  }
  let t6;
  if ($[18] !== actionButtonProps || $[19] !== showStandaloneButton) {
    t6 = showStandaloneButton && <GiftActionButton {...actionButtonProps} />;
    $[18] = actionButtonProps;
    $[19] = showStandaloneButton;
    $[20] = t6;
  } else {
    t6 = $[20];
  }
  let t7;
  if ($[21] !== actionButtonProps || $[22] !== badge || $[23] !== showTooltipAction || $[24] !== t || $[25] !== tooltipMessage) {
    t7 = showTooltipAction && <div className="flex gap-snug-lg">{badge && <Badge label={t(badge.label)} color={badge.color as 'positive' | 'brand'} iconBefore={badge.icon} />}<GiftActionButton {...actionButtonProps} /><TooltipInfo contentClassName="label-md lg:label-lg" align="end" size="sm" alignOffset={-12} sideOffset={-4} collisionPadding={16}>{tooltipMessage}</TooltipInfo></div>;
    $[21] = actionButtonProps;
    $[22] = badge;
    $[23] = showTooltipAction;
    $[24] = t;
    $[25] = tooltipMessage;
    $[26] = t7;
  } else {
    t7 = $[26];
  }
  let t8;
  if ($[27] !== actionButtonProps || $[28] !== cartItem || $[29] !== onGiftRemove || $[30] !== t) {
    t8 = cartItem && <div className="flex flex-row items-center gap-comfy-sm label-potent label-xl">{t("core:numbers.currencyCustom", {
        amount: cartItem.amount,
        useNegativeParens: true
      })}<GiftActionButton {...actionButtonProps} /><GiftRemoveDialog onRemove={async () => {
        if (!cartItem.cartId || !onGiftRemove) {
          return;
        }
        await onGiftRemove(cartItem.cartId);
      }} openTrigger={<ButtonStyle size="lg" variant="tertiary" purpose="neutral" iconOnly={true}><button type="button"><ArkIcon aria-label={t("my:giftCommitmentCard.removeGiftDialog.remove")} iconName="trash-01" /></button></ButtonStyle>} /></div>;
    $[27] = actionButtonProps;
    $[28] = cartItem;
    $[29] = onGiftRemove;
    $[30] = t;
    $[31] = t8;
  } else {
    t8 = $[31];
  }
  let t9;
  if ($[32] !== t5 || $[33] !== t6 || $[34] !== t7 || $[35] !== t8) {
    t9 = <>{t5}{t6}{t7}{t8}</>;
    $[32] = t5;
    $[33] = t6;
    $[34] = t7;
    $[35] = t8;
    $[36] = t9;
  } else {
    t9 = $[36];
  }
  return t9;
};